'use client';

import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/utils/utils';
import { formatMNT } from '@/lib/utils/formatters';
import { useCvAiUsage } from '@/features/cv';
import { useAuthMe } from '@/features/user';
import {
  useUpdateZangiaPlusAdditional,
  useZangiaPlusReport,
} from '@/features/jobs/api/jobs.queries';
import {
  formatPlusDate,
  formatPlusDateTime,
  plusDurationDays,
  plusHistoryPeriod,
  plusPaymentLabel,
  plusRemindStorageKey,
  remainingPlusDays,
  toPlusAdditionalKey,
} from '@/features/jobs/lib/zangiaPlus';
import { PlusFeatureCheck } from './PlusFeatureCheck';
import {
  isPlusSelectableFeatureId,
  plusCopy,
  plusFeatures,
  type PlusSelectableFeatureId,
} from './plusCopy';
import type { AiUsageQuota } from '@/shared/cv/cv.ai.dto';
import { Crown } from 'lucide-react';

const QUOTA_FEATURE_USAGE_KEY = {
  career: 'aiCareer',
  photo: 'aiEnhancePhoto',
  cvAdvice: 'aiSuggest',
} as const;

function StatusBadge({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className={cn(
        'rounded-full px-2.5 py-0.5 text-xs font-semibold',
        active ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'
      )}
    >
      {label}
    </span>
  );
}

function StatCard({
  label,
  value,
  hint,
  valueClassName,
}: {
  label: string;
  value: string;
  hint: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl bg-gray-50 px-4 py-4">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      <span className={cn('text-lg font-bold', valueClassName)}>{value}</span>
      <span className="text-xs text-gray-500">{hint}</span>
    </div>
  );
}

function UsageBar({ ratio }: { ratio: number }) {
  const width = Math.min(100, Math.max(0, Math.round(ratio * 100)));
  return (
    <div className="h-1.5 w-28 overflow-hidden rounded-full bg-primary-50">
      <div
        className="h-full rounded-full bg-primary"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function quotaRemaining(quota?: AiUsageQuota, fallbackTotal = 0) {
  const total = quota?.total ?? fallbackTotal;
  const remaining = quota?.avialable ?? 0;
  return { total, remaining };
}

function extrasFromReport(cvtop: number, suggesttop: number) {
  const extras: PlusSelectableFeatureId[] = [];
  if (cvtop > 0) extras.push('cvtop');
  if (suggesttop > 0) extras.push('suggesttop');
  return extras;
}

export default function ZangiaPlusSettingsTab({
  isEnglish = false,
}: {
  isEnglish?: boolean;
}) {
  const t = isEnglish ? plusCopy.en : plusCopy.mn;
  const { data: user } = useAuthMe();
  const { data: report, isLoading: isReportLoading } = useZangiaPlusReport();
  const { mutateAsync: updateAdditional, isPending: isSavingExtras } =
    useUpdateZangiaPlusAdditional();

  const expireUnix = report?.expire ?? 0;
  const startUnix = report?.time ?? 0;
  const hasPlus = expireUnix > dayjs().unix();
  const { data: usage, isLoading: isUsageLoading } = useCvAiUsage(hasPlus);

  const history = useMemo(
    () =>
      [...(report?.history ?? [])].sort(
        (a, b) => Number(b.time) - Number(a.time)
      ),
    [report?.history]
  );
  const latestHistory = history[0];
  const durationDays = plusDurationDays(startUnix, expireUnix);
  const remainingDays = hasPlus ? remainingPlusDays(expireUnix) : 0;
  const usedRatio =
    hasPlus && durationDays > 0
      ? Math.min(1, Math.max(0, 1 - remainingDays / durationDays))
      : 0;

  const savedExtras = useMemo(
    () => extrasFromReport(report?.cvtop ?? 0, report?.suggesttop ?? 0),
    [report?.cvtop, report?.suggesttop]
  );

  const [remind, setRemind] = useState(true);
  const [extras, setExtras] = useState<PlusSelectableFeatureId[]>([]);
  const [extrasDirty, setExtrasDirty] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    const stored = localStorage.getItem(plusRemindStorageKey(user.id));
    if (stored !== null) {
      setRemind(stored === '1');
    }
  }, [user?.id]);

  useEffect(() => {
    if (!report || extrasDirty) return;
    setExtras(extrasFromReport(report.cvtop, report.suggesttop));
    // extrasDirty intentionally omitted: clearing dirty after save must not
    // reset checkboxes from a still-stale report before refetch finishes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.cvtop, report?.suggesttop]);

  const extrasChanged = useMemo(() => {
    if (extras.length !== savedExtras.length) return true;
    return extras.some((id) => !savedExtras.includes(id));
  }, [extras, savedExtras]);

  const handleRemindChange = (checked: boolean) => {
    setRemind(checked);
    if (user?.id) {
      localStorage.setItem(plusRemindStorageKey(user.id), checked ? '1' : '0');
    }
  };

  const toggleExtra = (id: PlusSelectableFeatureId) => {
    setExtrasDirty(true);
    setExtras((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const handleSaveExtras = async () => {
    if (!hasPlus) return;

    const changed = plusFeatures
      .map((feature) => feature.id)
      .filter(isPlusSelectableFeatureId)
      .filter((id) => extras.includes(id) !== savedExtras.includes(id));

    if (changed.length === 0) return;

    try {
      await updateAdditional(changed.map(toPlusAdditionalKey));
      setExtrasDirty(false);
      toast.success(t.extrasSaveSuccess);
    } catch {
      // Error toast is handled by useUpdateZangiaPlusAdditional
    }
  };

  if (isReportLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="h-8 w-48 animate-pulse rounded-lg bg-gray-200" />
        <div className="h-56 animate-pulse rounded-2xl bg-white" />
        <div className="h-64 animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }

  const packageTitle = latestHistory?.descr || t.inactiveTitle;
  const packagePrice =
    latestHistory != null ? formatMNT(latestHistory.amount) : null;

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-5 rounded-2xl bg-white p-5 md:p-7">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-700 text-white">
              <Crown className="h-4 w-4" />
            </span>
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xl font-bold text-gray-900">
                  {hasPlus ? packageTitle : t.inactiveTitle}
                </span>
                <StatusBadge
                  active={hasPlus}
                  label={hasPlus ? t.active : t.inactive}
                />
              </div>
              <span className="text-sm text-gray-500">
                {hasPlus && packagePrice
                  ? `${packagePrice} · ${packageTitle}`
                  : t.inactiveSubtitle}
              </span>
            </div>
          </div>
        </div>

        {hasPlus ? (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <StatCard
                label={t.purchasedOn}
                value={formatPlusDate(startUnix)}
                hint={
                  startUnix
                    ? `${dayjs.unix(startUnix).format('HH:mm')}${
                        latestHistory
                          ? ` · ${plusPaymentLabel(latestHistory.type, isEnglish)}`
                          : ''
                      }`
                    : '—'
                }
              />
              <StatCard
                label={t.validUntil}
                value={`${formatPlusDate(expireUnix)} ${t.validUntilSuffix}`.trim()}
                hint={t.endsAt}
              />
              <StatCard
                label={t.remainingTime}
                value={`${remainingDays} ${t.remainingDays}`}
                hint={
                  durationDays
                    ? `${durationDays} ${t.ofDays}`
                    : t.remainingDays
                }
                valueClassName="text-primary"
              />
            </div>

            <div className="flex flex-col gap-2">
              <div
                role="progressbar"
                aria-label={t.remainingTime}
                aria-valuenow={Math.round(usedRatio * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
                className="h-2 overflow-hidden rounded-full bg-primary-50"
              >
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${Math.round(usedRatio * 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>
                  {t.started}: {formatPlusDate(startUnix)}
                </span>
                <span>
                  {t.ends}: {formatPlusDate(expireUnix)}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 border-t border-gray-100 pt-4">
              <div className="flex flex-col gap-0.5">
                <label htmlFor="plus-remind" className="text-sm font-semibold">
                  {t.remindLabel}
                </label>
                <span className="text-xs text-gray-500">{t.remindHint}</span>
              </div>
              <input
                id="plus-remind"
                type="checkbox"
                checked={remind}
                onChange={(event) => handleRemindChange(event.target.checked)}
                className="h-5 w-5 accent-primary"
              />
            </div>
          </>
        ) : null}
      </section>

      <section className="flex flex-col rounded-2xl bg-white p-5 md:p-7">
        <h2 className="mb-2 text-lg font-bold">{t.usageTitle}</h2>
        {plusFeatures.map((feature, index) => {
          const isLast = index === plusFeatures.length - 1;
          const rowClass = cn(
            'flex items-center justify-between gap-4 py-3.5',
            !isLast && 'border-b border-gray-100'
          );
          const title = isEnglish ? feature.en : feature.mn;
          const hint =
            'hintMn' in feature
              ? isEnglish
                ? feature.hintEn
                : feature.hintMn
              : null;

          if (isPlusSelectableFeatureId(feature.id)) {
            const extraId = feature.id;
            const checked = extras.includes(extraId);
            return (
              <div key={feature.id} className={rowClass}>
                <label className="flex min-w-0 items-start gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isSavingExtras}
                    onChange={() => toggleExtra(extraId)}
                    className="mt-0.5 h-5 w-5 shrink-0 accent-primary disabled:opacity-50"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm text-gray-900">{title}</span>
                    {hint ? (
                      <span className="text-xs text-gray-500">{hint}</span>
                    ) : null}
                  </span>
                </label>
                <span className="shrink-0 text-sm font-bold text-primary">
                  {t.unlimited}
                </span>
              </div>
            );
          }

          if (feature.unlimited) {
            return (
              <div key={feature.id} className={rowClass}>
                <div className="flex min-w-0 items-center gap-2.5">
                  <PlusFeatureCheck variant="included" />
                  <span className="text-sm text-gray-900">{title}</span>
                </div>
                <span className="shrink-0 text-sm font-bold text-primary">
                  {t.unlimited}
                </span>
              </div>
            );
          }

          const usageKey =
            feature.id in QUOTA_FEATURE_USAGE_KEY
              ? QUOTA_FEATURE_USAGE_KEY[
                  feature.id as keyof typeof QUOTA_FEATURE_USAGE_KEY
                ]
              : null;
          const quota = usageKey && usage ? usage[usageKey] : undefined;
          const { total, remaining } = hasPlus
            ? quotaRemaining(quota, feature.count ?? 0)
            : { total: feature.count ?? 0, remaining: 0 };
          const usedRatioForFeature =
            hasPlus && total > 0 ? (total - remaining) / total : 0;

          return (
            <div key={feature.id} className={rowClass}>
              <div className="flex min-w-0 items-center gap-2.5">
                <PlusFeatureCheck variant="included" />
                <span className="text-sm text-gray-900">{title}</span>
              </div>
              <div className="flex items-center gap-3">
                {isUsageLoading && hasPlus ? (
                  <div className="h-1.5 w-28 animate-pulse rounded-full bg-gray-100" />
                ) : (
                  <UsageBar ratio={usedRatioForFeature} />
                )}
                <span className="w-28 text-right text-sm font-bold text-primary">
                  {remaining} / {total} {t.remainingCount}
                </span>
              </div>
            </div>
          );
        })}

        <div className="mt-3 flex flex-col gap-3 rounded-xl bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-gray-500">{t.extrasLockedHint}</span>
          <Button
            variant="primary"
            className="h-11 shrink-0 rounded-full px-6 text-sm font-bold"
            loading={isSavingExtras}
            disabled={isSavingExtras || !hasPlus || !extrasChanged}
            onClick={() => {
              void handleSaveExtras();
            }}
          >
            {t.save}
          </Button>
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-2xl bg-white p-5 md:p-7">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold">{t.historyTitle}</h2>
        </div>
        {history.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            {t.historyEmpty}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-3xl border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="border-b border-gray-100 py-2.5 font-semibold">
                    {t.historyPurchased}
                  </th>
                  <th className="border-b border-gray-100 py-2.5 font-semibold">
                    {t.historyPackage}
                  </th>
                  <th className="border-b border-gray-100 py-2.5 font-semibold">
                    {t.historyValid}
                  </th>
                  <th className="border-b border-gray-100 py-2.5 font-semibold">
                    {t.historyPayment}
                  </th>
                  <th className="border-b border-gray-100 py-2.5 text-right font-semibold">
                    {t.historyAmount}
                  </th>
                  <th className="border-b border-gray-100 py-2.5 text-right font-semibold">
                    {t.historyStatus}
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 5).map((item, index) => {
                  const isCurrent =
                    hasPlus && index === 0 && item.expire >= dayjs().unix();
                  return (
                    <tr key={`${item.time}-${item.expire}-${index}`}>
                      <td className="border-b border-gray-100 py-3.5">
                        {formatPlusDateTime(item.time)}
                      </td>
                      <td className="border-b border-gray-100 py-3.5 font-semibold">
                        {item.descr || 'Zangia Plus'}
                      </td>
                      <td className="border-b border-gray-100 py-3.5">
                        {plusHistoryPeriod(item.time, item.expire)}
                      </td>
                      <td className="border-b border-gray-100 py-3.5">
                        {plusPaymentLabel(item.type, isEnglish)}
                      </td>
                      <td className="border-b border-gray-100 py-3.5 text-right font-semibold">
                        {formatMNT(item.amount)}
                      </td>
                      <td className="border-b border-gray-100 py-3.5 text-right">
                        <StatusBadge
                          active={isCurrent}
                          label={isCurrent ? t.active : t.expired}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
